// Resizable table columns, remembered per table (Chris, 2026-10-15: some tables stretch so wide that a
// row is hard to follow, such as Hours by code under Time Keeping).
//
// Every <table> on a screen gets a drag handle on the right edge of each header cell. The first drag
// fixes the table's current column widths, so the dragged column changes and the others stay put.
// Widths are kept in this browser's localStorage, keyed by the screen and the table's column headings,
// so a table gets its widths back whenever it shows the same columns. Double-click a handle to go back
// to automatic widths. Screens need no changes: tables are found as they render.

import { useEffect } from "react";

const PREFIX = "basecamp.cols:";
const MIN = 40;

function headerCells(table: HTMLTableElement): HTMLTableCellElement[] {
  const row = table.tHead?.rows[0];
  return row ? [...row.cells] : [];
}

function keyOf(screen: string, cells: HTMLTableCellElement[]) {
  return `${PREFIX}${screen}:${cells.map((c) => (c.textContent ?? "").trim() || "·").join("|")}`;
}

function load(key: string): number[] | null {
  try {
    const raw = localStorage.getItem(key);
    const widths = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(widths) && widths.every((w) => typeof w === "number") ? (widths as number[]) : null;
  } catch {
    return null;
  }
}

function store(key: string, widths: number[] | null) {
  try {
    if (widths) localStorage.setItem(key, JSON.stringify(widths));
    else localStorage.removeItem(key);
  } catch {
    // Storage blocked: the widths last until the table re-renders.
  }
}

function apply(table: HTMLTableElement, cells: HTMLTableCellElement[], widths: number[]) {
  cells.forEach((c, i) => (c.style.width = `${widths[i]}px`));
  table.style.tableLayout = "fixed";
  table.style.width = `${widths.reduce((a, b) => a + b, 0)}px`;
  table.style.minWidth = "0";
  table.classList.add("cols-sized");
}

function clear(table: HTMLTableElement, cells: HTMLTableCellElement[]) {
  cells.forEach((c) => (c.style.width = ""));
  table.style.tableLayout = "";
  table.style.width = "";
  table.style.minWidth = "";
  table.classList.remove("cols-sized");
}

function enhance(table: HTMLTableElement, screen: string) {
  const cells = headerCells(table);
  if (cells.length < 2) return;
  const key = keyOf(screen, cells);
  // Re-run when the headings change (another tab, a column shown or hidden).
  if (table.dataset.colsKey === key) return;
  table.dataset.colsKey = key;
  const saved = load(key);
  if (saved && saved.length === cells.length) apply(table, cells, saved);
  else if (table.classList.contains("cols-sized")) clear(table, cells);

  cells.forEach((cell, i) => {
    cell.classList.add("col-resizable");
    let handle = cell.querySelector<HTMLSpanElement>(":scope > .col-resizer");
    if (!handle) {
      handle = document.createElement("span");
      handle.className = "col-resizer";
      handle.setAttribute("aria-hidden", "true");
      handle.title = "Drag to resize; double-click for automatic widths";
      cell.appendChild(handle);
    }
    handle.onclick = (ev) => ev.stopPropagation(); // a sortable header shouldn't sort on a resize
    handle.ondblclick = (ev) => {
      ev.stopPropagation();
      const now = headerCells(table);
      store(table.dataset.colsKey ?? key, null);
      clear(table, now);
    };
    handle.onpointerdown = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const now = headerCells(table);
      const widths = now.map((c) => c.getBoundingClientRect().width);
      apply(table, now, widths);
      const startX = ev.clientX;
      const startW = widths[i];
      const target = ev.currentTarget as HTMLElement;
      target.setPointerCapture(ev.pointerId);
      document.body.classList.add("col-resizing");
      const move = (e: PointerEvent) => {
        widths[i] = Math.max(MIN, Math.round(startW + e.clientX - startX));
        apply(table, now, widths);
      };
      const up = () => {
        target.removeEventListener("pointermove", move);
        target.removeEventListener("pointerup", up);
        target.removeEventListener("pointercancel", up);
        document.body.classList.remove("col-resizing");
        store(table.dataset.colsKey ?? key, widths);
      };
      target.addEventListener("pointermove", move);
      target.addEventListener("pointerup", up);
      target.addEventListener("pointercancel", up);
    };
  });
}

/** Watches the screen area and makes each table's columns resizable as it renders. */
export function useResizableColumns(container: () => HTMLElement | null, screen: string) {
  useEffect(() => {
    const root = container();
    if (!root) return;
    let queued = 0;
    const scan = () => {
      queued = 0;
      root.querySelectorAll("table").forEach((t) => enhance(t as HTMLTableElement, screen));
    };
    scan();
    const observer = new MutationObserver(() => {
      if (!queued) queued = requestAnimationFrame(scan);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      if (queued) cancelAnimationFrame(queued);
    };
  }, [container, screen]);
}
